import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { mockInvoiceService } from '@/services/mock/MockInvoiceService';
import { InvoiceRecord, InvoiceFilterState, InvoiceMetricsSummary, CreateInvoiceInput } from '@/types/domain/invoice-management';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { formatInr, formatDate } from '@/lib/utils';
import {
  Receipt,
  RefreshCw,
  Search,
  Eye,
  Pencil,
  Trash2,
  Database,
  AlertTriangle,
  Plus,
  Download,
  CheckCircle2,
  Clock,
  IndianRupee,
  Copy,
  Loader2,
  X,
  Scale,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { InvoiceKpiCards } from '@/features/invoices/components/InvoiceKpiCards';
import { InvoiceCreateModal } from '@/features/invoices/components/InvoiceCreateModal';
import { usePermission } from '@/hooks/usePermission';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { InvoiceDetailPage } from './InvoiceDetailPage';

export const InvoicesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { can } = usePermission();
  const [metrics, setMetrics] = useState<InvoiceMetricsSummary | null>(null);
  const [records, setRecords] = useState<InvoiceRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [matchingInvoiceId, setMatchingInvoiceId] = useState<string | null>(null);

  const initialPoId = searchParams.get('poId') || undefined;
  const initialGrnId = searchParams.get('grnId') || undefined;
  const initialPoNumber = searchParams.get('poNumber') || undefined;

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setIsCreateModalOpen(true);
    }
  }, [searchParams]);

  // Cancellation / Rejection Modal State
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedInvoiceForDrawer, setSelectedInvoiceForDrawer] = useState<string | null>(null);

  const [filters, setFilters] = useState<InvoiceFilterState>({
    search: '',
    status: 'ALL',
    matchingStatus: 'ALL',
    paymentStatus: 'ALL',
    exceptionCode: 'ALL',
    vendor: 'ALL',
    plant: 'ALL',
    sortBy: 'invoiceDate',
    sortOrder: 'desc',
    page: 1,
    pageSize: 25,
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [m, invRes] = await Promise.all([
        mockInvoiceService.getInvoiceMetrics(),
        mockInvoiceService.getInvoices(filters),
      ]);
      setMetrics(m);
      setRecords(invRes.items);
      setTotal(invRes.total);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!invoiceToDelete) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [invoiceToDelete]);

  const handleExportCsv = () => {
    if (records.length === 0) return;
    const headers = [
      'Invoice Number',
      'Vendor Invoice #',
      'SAP MIRO Doc #',
      'PO Number',
      'GRN Number',
      'Supplier',
      'Total Amount (INR)',
      'Invoice Date',
      'Due Date',
      'Matching Status',
      'Payment Status',
      'Invoice Status',
      'Overdue Days',
    ];
    const rows = records.map((r) => [
      r.invoiceNumber,
      r.vendorInvoiceNumber,
      r.sapMiroNumber || 'N/A',
      r.poNumber,
      r.grnNumber || 'N/A',
      `"${r.vendorName}"`,
      r.totalAmount,
      r.invoiceDate,
      r.dueDate,
      r.matchingStatus,
      r.paymentStatus,
      r.status,
      r.daysOverdue,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Invoices_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreateInvoice = async (input: CreateInvoiceInput) => {
    const created = await mockInvoiceService.createInvoice(input);
    navigate(ROUTES.INVOICES.DETAILS(created.id));
  };

  const handleTriggerMatch = async (invId: string) => {
    try {
      setMatchingInvoiceId(invId);
      await mockInvoiceService.runThreeWayMatch(invId);
      await loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Match evaluation failed');
    } finally {
      setMatchingInvoiceId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!invoiceToDelete) return;
    try {
      setIsDeleting(true);
      await mockInvoiceService.cancelInvoice(invoiceToDelete.id, 'Administrative cancellation requested by finance controller.');
      setInvoiceToDelete(null);
      loadData();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to cancel invoice.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getMatchBadge = (matchingStatus: InvoiceRecord['matchingStatus']) => {
    switch (matchingStatus) {
      case 'MATCHED':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 whitespace-nowrap">
            <CheckCircle2 className="h-3 w-3" /> 3-Way Matched
          </span>
        );
      case 'QUANTITY_VARIANCE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 whitespace-nowrap">
            <AlertTriangle className="h-3 w-3" /> Qty Variance
          </span>
        );
      case 'PRICE_VARIANCE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20 whitespace-nowrap">
            <AlertTriangle className="h-3 w-3" /> Price Variance
          </span>
        );
      case 'MISSING_GRN':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20 whitespace-nowrap">
            <AlertTriangle className="h-3 w-3" /> Missing GRN
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins bg-muted text-foreground-secondary border-border whitespace-nowrap">
            {matchingStatus.replace(/_/g, ' ')}
          </span>
        );
    }
  };

  const columns: ColumnDef<InvoiceRecord>[] = [
    {
      header: 'Invoice # & Reference',
      accessorKey: 'invoiceNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins min-w-[150px]">
          <div className="flex items-center gap-2">
            <span
              onClick={() => navigate(ROUTES.INVOICES.DETAILS(row.id))}
              className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
            >
              {row.invoiceNumber}
            </span>
            {row.duplicateRisk === 'DUPLICATE_CONFIRMED' && (
              <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-rose-600 text-white flex items-center gap-1">
                <Copy className="h-3 w-3" /> Duplicate
              </span>
            )}
            {row.isOverdue && (
              <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 flex items-center gap-1">
                <Clock className="h-3 w-3" /> {row.daysOverdue}d Overdue
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-foreground-muted font-mono whitespace-nowrap">
            <span>Ref: {row.vendorInvoiceNumber}</span>
            <span>•</span>
            {row.sapMiroNumber ? (
              <span className="text-purple-600 dark:text-purple-400 font-bold flex items-center gap-0.5">
                <Database className="h-3 w-3" /> MIRO: {row.sapMiroNumber}
              </span>
            ) : (
              <span>MIRO: Pending</span>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Supplier & Plant',
      accessorKey: 'vendorName',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col min-w-[180px] max-w-[210px] xl:max-w-[230px] font-poppins">
          <span className="font-bold text-foreground text-sm truncate block" title={row.vendorName}>
            {row.vendorName}
          </span>
          <span className="text-xs text-foreground-muted font-mono truncate block">
            {row.plantCode} • {row.vendorCode}
          </span>
        </div>
      ),
    },
    {
      header: 'PO & GRN Tracing',
      cell: (row) => (
        <div className="flex flex-col text-xs font-mono font-poppins space-y-0.5 min-w-[120px]">
          <span
            onClick={(e) => {
              e.stopPropagation();
              navigate(ROUTES.PURCHASE_ORDERS.DETAILS(row.poId));
            }}
            className="text-primary font-bold hover:underline cursor-pointer block"
          >
            {row.poNumber}
          </span>
          {row.grnNumber ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                navigate(ROUTES.GRN.DETAILS(row.grnId || row.grnNumber || ''));
              }}
              className="text-emerald-600 dark:text-emerald-400 font-bold hover:underline cursor-pointer block text-[11px]"
            >
              {row.grnNumber}
            </span>
          ) : (
            <span className="text-rose-600 dark:text-rose-400 text-[10px] font-bold block">No GRN</span>
          )}
        </div>
      ),
    },
    {
      header: 'Billed Amount',
      accessorKey: 'totalAmount',
      sortable: true,
      cell: (row) => (
        <div className="font-poppins min-w-[130px]">
          <span className="font-bold text-foreground font-mono whitespace-nowrap text-sm block">
            {formatInr(row.totalAmount)}
          </span>
          <span className="text-[11px] text-foreground-muted block">
            Tax: {formatInr(row.taxAmount)}
          </span>
        </div>
      ),
    },
    {
      header: '3-Way Match',
      cell: (row) => (
        <div className="font-poppins space-y-1 min-w-[150px]">
          {getMatchBadge(row.matchingStatus)}
          {row.exceptions.filter((e) => e.status !== 'RESOLVED').length > 0 && (
            <span className="text-[11px] font-bold text-rose-600 dark:text-rose-400 block">
              {row.exceptions.filter((e) => e.status !== 'RESOLVED').length} active exception(s)
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Payment Status',
      accessorKey: 'paymentStatus',
      cell: (row) => (
        <div className="space-y-0.5 font-poppins text-xs min-w-[140px]">
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${
              row.paymentStatus === 'PAID'
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                : row.paymentStatus === 'READY_FOR_PAYMENT'
                ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20'
                : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
            }`}
          >
            <IndianRupee className="h-3 w-3" /> {row.paymentStatus.replace(/_/g, ' ')}
          </span>
          <span className="text-[10px] text-foreground-muted block whitespace-nowrap">
            Due: {formatDate(row.dueDate)}
          </span>
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => {
        const isRunningMatch = matchingInvoiceId === row.id;
        return (
          <div
            className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[140px]"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedInvoiceForDrawer(row.id)}
              title="View Invoice Dossier (Slide-over)"
              className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
            >
              <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.INVOICES.EDIT(row.id))}
              title="Edit Financial Baseline"
              className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
            >
              <Pencil className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              disabled={isRunningMatch}
              onClick={() => handleTriggerMatch(row.id)}
              title="Run 3-Way Match Verification"
              className="h-8 w-8 p-0 rounded-lg text-purple-600 hover:text-purple-700 hover:bg-purple-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
            >
              {isRunningMatch ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Scale className="h-4 w-4" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setInvoiceToDelete(row)}
              title="Cancel / Reject Invoice"
              className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Receipt className="h-7 w-7 text-primary" /> Accounts Payable & 3-Way Match Command Center
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Automated PO vs GRN vs Invoice reconciliation, multi-dimensional duplicate detection, exception resolution, and SAP MIRO posting.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>

          {can('INVOICE_MATCH') && (
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Ingest / Simulate Invoice
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards Strip */}
      {metrics && (
        <InvoiceKpiCards
          metrics={metrics}
          onSelectFilter={(filterType, val) => {
            if (filterType === 'status') {
              setFilters((prev) => ({ ...prev, status: val }));
            } else if (filterType === 'matchingStatus') {
              setFilters((prev) => ({ ...prev, matchingStatus: val }));
            } else if (filterType === 'paymentStatus') {
              setFilters((prev) => ({ ...prev, paymentStatus: val }));
            } else if (filterType === 'isOverdue') {
              setFilters((prev) => ({ ...prev, isOverdue: true }));
            }
          }}
        />
      )}

      {/* Main Card with Squeezed Status Tabs & Search */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Squeezed Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Invoices' },
                { key: 'MATCHED', label: '3-Way Matched' },
                { key: 'EXCEPTION', label: 'Exceptions' },
                { key: 'READY_FOR_PAYMENT', label: 'Ready for Payment' },
                { key: 'PAID', label: 'Paid' },
                { key: 'CANCELLED', label: 'Cancelled' },
              ].map((tab) => {
                const isActive = filters.status === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilters((prev) => ({ ...prev, status: tab.key }))}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Search Input */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Search Invoice #, PO #, Ref #, Supplier..."
                className="w-full h-9 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-auto">
          <DataTable
            data={records}
            columns={columns}
            isLoading={isLoading}
            pageSize={25}
            totalCount={total}
            hideSearch={true}
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-auto custom-scrollbar"
            emptyTitle="No Invoices Found"
            emptyDescription="There are no accounts payable invoices matching your current filter criteria."
            onRowClick={(row) => navigate(ROUTES.INVOICES.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Inward Simulation Modal */}
      <InvoiceCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={handleCreateInvoice}
        initialPoId={initialPoId}
        initialGrnId={initialGrnId}
        initialPoNumber={initialPoNumber}
      />

      {/* Cancel / Rejection Confirmation Modal (Mounted via createPortal to document.body) */}
      {invoiceToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setInvoiceToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Cancel & Reject Invoice?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setInvoiceToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Invoice Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Invoice Number
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {invoiceToDelete.invoiceNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    PO Reference
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {invoiceToDelete.poNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Supplier
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {invoiceToDelete.vendorName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Billed Total
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {formatInr(invoiceToDelete.totalAmount)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Cancelling this invoice revokes any approved payment readiness, clears scheduled treasury batches, and records an audited cancellation event.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setInvoiceToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Keep Invoice
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmCancel}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Cancelling...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Confirm Cancellation</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for Invoice Dossier */}
      <SlideOverDrawer
        isOpen={Boolean(selectedInvoiceForDrawer)}
        onClose={() => setSelectedInvoiceForDrawer(null)}
        title="Accounts Payable Invoice Dossier"
        subtitle="Three-way reconciliation, line item matching, tax verification, and SAP MIRO posting"
        fullPageUrl={selectedInvoiceForDrawer ? ROUTES.INVOICES.DETAILS(selectedInvoiceForDrawer) : undefined}
      >
        {selectedInvoiceForDrawer && (
          <InvoiceDetailPage
            recordId={selectedInvoiceForDrawer}
            isDrawer={true}
            onClose={() => setSelectedInvoiceForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
